import type { WizardIndex, WizardStep, WizardTree } from './types';

/**
 * Flatten a tree into key → step and key → parent maps, validating every reference on the way.
 *
 * Duplicate keys throw rather than shadow: a tree is authored by hand, and a repeated key would make
 * `parentOf` depend on traversal order — the kind of bug that surfaces as a step navigating to the
 * wrong sibling months later.
 *
 * A `next` or `cancelTo` naming a step that isn't in the tree throws too. Admitting one puts the
 * wizard on a key with no step, where the body renders blank, the footer shows a live primary (a
 * missing step is not `kind === 'menu'`) and no Back, and both `nextKey` and `prevKey` return null —
 * unrecoverable without a consumer calling `reset()`. Validating here keeps every navigation
 * function free of defensive membership checks, and because a tree is authored at module scope every
 * throw is a developer-visible error at import time rather than a user-visible one.
 */
export function indexTree<K extends string>(tree: WizardTree<K>): WizardIndex<K> {
  const byKey = new Map<K, WizardStep<K>>();
  const parentOf = new Map<K, K>();

  const visit = (step: WizardStep<K>, parent: K | null): void => {
    if (byKey.has(step.key)) throw new Error(`Wizard tree has a duplicate step key "${step.key}"`);
    byKey.set(step.key, step);
    if (parent !== null) parentOf.set(step.key, parent);
    for (const child of step.children ?? []) visit(child, step.key);
  };

  for (const step of tree) visit(step, null);

  // Cross-references are resolved after the traversal, not during it, so a step may name a target
  // that appears later in the tree.
  const nextFrom = new Map<K, K>();
  for (const step of byKey.values()) {
    if (step.cancelTo !== undefined && !byKey.has(step.cancelTo)) {
      throw new Error(`Wizard step "${step.key}" has an unknown cancelTo target "${step.cancelTo}"`);
    }
    if (step.next === undefined) continue;
    if (!byKey.has(step.next)) {
      throw new Error(`Wizard step "${step.key}" has an unknown next target "${step.next}"`);
    }

    // The reverse edge is Back's answer only when it is the ONLY way in. Two ways in means the right
    // answer depends on which one the user took, and this model records no path — so refuse the tree
    // instead of choosing an arm and being silently wrong half the time.
    const rival = nextFrom.get(step.next);
    if (rival !== undefined) {
      throw new Error(
        `Wizard step "${step.next}" is the next target of both "${rival}" and "${step.key}", so Back is ambiguous`,
      );
    }
    const parent = parentOf.get(step.next);
    if (parent !== undefined) {
      throw new Error(
        `Wizard step "${step.next}" is the next target of "${step.key}" but is also a child of "${parent}", so Back is ambiguous`,
      );
    }
    nextFrom.set(step.next, step.key);
  }

  // `prevKey` follows the reverse edge, so an edge pointing back into its own ancestry would make
  // Back — and `exitKey`, which walks the same chain — loop forever. Only a `next` target can start
  // such a loop, so those are the only chains worth walking, and checking here is what lets both
  // navigation functions stay free of cycle guards.
  for (const target of nextFrom.keys()) {
    const seen = new Set<K>([target]);
    let cursor = parentOf.get(target) ?? nextFrom.get(target);
    while (cursor !== undefined) {
      if (seen.has(cursor)) {
        throw new Error(`Wizard tree has a back-navigation cycle through step "${cursor}"`);
      }
      seen.add(cursor);
      cursor = parentOf.get(cursor) ?? nextFrom.get(cursor);
    }
  }

  return { byKey, parentOf, nextFrom };
}

/** The chain of keys from a root down to `key`. Empty when the key is not in the tree. */
export function pathTo<K extends string>(index: WizardIndex<K>, key: K): K[] {
  if (!index.byKey.has(key)) return [];
  const path: K[] = [key];
  let cursor = index.parentOf.get(key);
  while (cursor !== undefined) {
    path.unshift(cursor);
    cursor = index.parentOf.get(cursor);
  }
  return path;
}
